import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import agentService from '../services/agentService';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/common/PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindows, faLinux, faApple } from '@fortawesome/free-brands-svg-icons';
import { faList, faTimes, faSpinner, faCheckCircle, faTimesCircle, faClock, faTerminal } from '@fortawesome/free-solid-svg-icons';

function AgentDashboard() {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    status: '',
    platform: '',
    approved: ''
  });
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentTasks, setAgentTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);

  const fetchAgents = useCallback(async () => {
    try {
      const [agentsData, statsData] = await Promise.all([
        agentService.getAgents(filter),
        agentService.getAgentStats()
      ]);
      setAgents(Array.isArray(agentsData) ? agentsData : []);
      setStats(statsData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAgents();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAgents, 30000);
    return () => clearInterval(interval);
  }, [fetchAgents]);

  const handleApprove = async (agentId) => {
    try {
      await agentService.approveAgent(agentId);
      fetchAgents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevoke = async (agentId) => {
    try {
      await agentService.revokeAgent(agentId);
      fetchAgents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (agentId) => {
    if (window.confirm(t('projects.deleteConfirm'))) {
      try {
        await agentService.deleteAgent(agentId);
        fetchAgents();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleViewQueue = async (agent) => {
    setSelectedAgent(agent);
    setShowQueueModal(true);
    setLoadingTasks(true);
    try {
      const tasks = await agentService.getAgentTasks(agent._id, { limit: 50 });
      setAgentTasks(Array.isArray(tasks) ? tasks : []);
    } catch (err) {
      setError(err.message);
      setAgentTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const generateKey = async () => {
    try {
      const result = await agentService.generateApiKey();
      setApiKey(result.apiKey);
      // Fetch version information
      try {
        const version = await agentService.getVersion();
        setVersionInfo(version);
      } catch (vErr) {
        console.error('Error fetching version:', vErr);
      }
      setShowDeployModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatLastSeen = (date) => {
    if (!date) return t('time.justNow');
    const d = new Date(date);
    const now = new Date();
    const diff = (now - d) / 1000; // seconds

    if (diff < 60) return t('time.justNow');
    if (diff < 3600) return t('time.minutesAgo', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('time.hoursAgo', { count: Math.floor(diff / 3600) });
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <PageHeader title={t('agents.title')} subtitle={t('agentDashboard.deployAndManage')}>
        <button
          onClick={generateKey}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          {t('agents.deployAgent')}
        </button>
      </PageHeader>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('agentDashboard.totalAgents')}</div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.total}</div>
          </div>
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('agents.online')}</div>
            <div className="text-2xl font-bold text-green-500">{stats.online}</div>
          </div>
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('agentDashboard.approved')}</div>
            <div className="text-2xl font-bold text-blue-500">{stats.approved}</div>
          </div>
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('agents.pending')}</div>
            <div className="text-2xl font-bold text-yellow-500">{stats.pending}</div>
          </div>
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('agentDashboard.tasksDone')}</div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.totalTasksCompleted}</div>
          </div>
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('agentDashboard.tasksFailed')}</div>
            <div className="text-2xl font-bold text-red-500">{stats.totalTasksFailed}</div>
          </div>
        </div>
      )}

      {/* Platform Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className={`rounded-lg p-4 flex items-center gap-3 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${darkMode ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
              <FontAwesomeIcon icon={faWindows} className="text-2xl text-blue-500" />
            </div>
            <div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Windows</div>
              <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.windows}</div>
            </div>
          </div>
          <div className={`rounded-lg p-4 flex items-center gap-3 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${darkMode ? 'bg-yellow-500/20' : 'bg-yellow-50'}`}>
              <FontAwesomeIcon icon={faLinux} className="text-2xl text-yellow-500" />
            </div>
            <div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Linux</div>
              <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.linux}</div>
            </div>
          </div>
          <div className={`rounded-lg p-4 flex items-center gap-3 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${darkMode ? 'bg-gray-500/20' : 'bg-gray-100'}`}>
              <FontAwesomeIcon icon={faApple} className="text-2xl text-gray-500" />
            </div>
            <div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>macOS</div>
              <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.darwin}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`rounded-lg p-4 mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white shadow border border-gray-200'}`}>
        <div className="flex flex-wrap gap-4">
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className={`px-3 py-2 rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900 border border-gray-300'}`}
          >
            <option value="">{t('common.all')} {t('common.status')}</option>
            <option value="online">{t('agents.online')}</option>
            <option value="busy">{t('agentDashboard.busy')}</option>
            <option value="offline">{t('agents.offline')}</option>
          </select>
          <select
            value={filter.platform}
            onChange={(e) => setFilter({ ...filter, platform: e.target.value })}
            className={`px-3 py-2 rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900 border border-gray-300'}`}
          >
            <option value="">{t('common.all')} {t('agents.platform')}</option>
            <option value="windows">Windows</option>
            <option value="linux">Linux</option>
            <option value="darwin">macOS</option>
          </select>
          <select
            value={filter.approved}
            onChange={(e) => setFilter({ ...filter, approved: e.target.value })}
            className={`px-3 py-2 rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900 border border-gray-300'}`}
          >
            <option value="">{t('common.all')}</option>
            <option value="true">{t('agentDashboard.approved')}</option>
            <option value="false">{t('agents.pending')}</option>
          </select>
          <button
            onClick={() => setFilter({ status: '', platform: '', approved: '' })}
            className={`px-3 py-2 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            {t('agentDashboard.clearFilters')}
          </button>
        </div>
      </div>

      {/* Agents Table */}
      <div className={`rounded-lg overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <table className="w-full">
          <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50 border-b border-gray-200'}>
            <tr>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('common.status')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('agentDashboard.agent')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('agents.platform')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('agentDashboard.user')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('agents.lastSeen')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('agentDashboard.tasks')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('agentDashboard.approved')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {agents.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center text-gray-500 py-8">
                  {t('agents.noAgents')}
                </td>
              </tr>
            ) : (
              agents.map((agent) => (
                <tr key={agent._id} className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 ${agentService.getStatusColor(agent.status)}`}>
                      <span className={`w-2 h-2 rounded-full ${
                        agent.status === 'online' ? 'bg-green-500' :
                        agent.status === 'busy' ? 'bg-yellow-500' :
                        agent.status === 'error' ? 'bg-red-500' : 'bg-gray-500'
                      }`}></span>
                      {t(`agents.${agent.status}`) || agent.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/agents/${agent._id}`} className={darkMode ? 'text-white hover:text-red-400' : 'text-gray-900 hover:text-red-600'}>
                      <div className="font-medium">{agent.name}</div>
                      <div className="text-xs text-gray-500">{agent.systemInfo?.hostname}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      <span>{agentService.getPlatformIcon(agent.platform)}</span>
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{agent.systemInfo?.os}</span>
                    </span>
                  </td>
                  <td className={`px-4 py-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {agent.systemInfo?.username}
                    {agent.systemInfo?.isAdmin && (
                      <span className="ml-1 text-xs text-red-400">(Admin)</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formatLastSeen(agent.lastSeen)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-green-500">{agent.stats?.tasksCompleted || 0}</span>
                    <span className="text-gray-500"> / </span>
                    <span className="text-red-500">{agent.stats?.tasksFailed || 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    {agent.isApproved ? (
                      <span className="text-green-500">{t('common.yes')}</span>
                    ) : (
                      <span className="text-yellow-500">{t('agents.pending')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Link
                        to={`/agents/${agent._id}`}
                        className="text-blue-400 hover:text-blue-300"
                        title={t('common.view')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => handleViewQueue(agent)}
                        className="text-purple-400 hover:text-purple-300"
                        title="View Queue & History"
                      >
                        <FontAwesomeIcon icon={faList} className="h-5 w-5" />
                      </button>
                      {!agent.isApproved ? (
                        <button
                          onClick={() => handleApprove(agent._id)}
                          className="text-green-400 hover:text-green-300"
                          title={t('agents.approve')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRevoke(agent._id)}
                          className="text-yellow-400 hover:text-yellow-300"
                          title={t('agentDashboard.revoke')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(agent._id)}
                        className="text-red-400 hover:text-red-300"
                        title={t('common.delete')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Deploy Modal */}
      {showDeployModal && (() => {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const serverUrl = `${wsProtocol}://${window.location.host}`;
        const fullConfig = {
          server_url: serverUrl,
          api_key: apiKey,
          agent_name: 'target-host-01',
          heartbeat_interval: 30,
          reconnect_interval: 5,
          max_reconnect_tries: -1,
          max_concurrent_jobs: 5,
          max_execution_time: 300,
          max_output_size: 10485760,
          enable_cleanup: true,
          sandbox_execution: false,
          allowed_commands: [],
          blocked_commands: [],
          log_level: 'info'
        };
        const configJson = JSON.stringify(fullConfig, null, 2);

        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('agents.deployAgent')}</h2>
              <button
                onClick={() => setShowDeployModal(false)}
                className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>API Key</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={apiKey}
                  readOnly
                  className={`flex-1 px-3 py-2 rounded font-mono text-sm ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900 border border-gray-300'}`}
                />
                <button
                  onClick={() => navigator.clipboard.writeText(apiKey)}
                  className={`px-3 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                >
                  {t('common.copy')}
                </button>
                <button
                  onClick={generateKey}
                  className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                  title="Generate a new API key"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                This key is shown once. Save it before closing this dialog.
              </p>
            </div>

            <div className="mb-4">
              <h3 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('agentDashboard.deploymentInstructions')}</h3>
              {versionInfo && (
                <div className={`mb-3 px-3 py-2 rounded text-sm ${darkMode ? 'bg-blue-900/30 text-blue-300 border border-blue-700' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                  <strong>Agent Version:</strong> {versionInfo.agent || versionInfo.version}
                  {versionInfo.buildDate && <span className="ml-2 opacity-75">({versionInfo.buildDate})</span>}
                </div>
              )}
              <div className={`rounded p-4 ${darkMode ? 'bg-gray-900' : 'bg-gray-100 border border-gray-200'}`}>
                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>1. {t('agentDashboard.downloadAgent')}:</p>
                <div className="flex gap-2 mb-4">
                  <button onClick={() => agentService.downloadAgent('windows')} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm inline-flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    Windows
                  </button>
                  <button onClick={() => agentService.downloadAgent('linux')} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm inline-flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    Linux
                  </button>
                  <button onClick={() => agentService.downloadAgent('darwin')} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm inline-flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    macOS
                  </button>
                </div>

                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>2. {t('agentDashboard.runAgent')}:</p>
                <div className={`rounded p-2 mb-4 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-300'}`}>
                  <code className="text-green-600 text-sm">
                    ./purpleteam-agent -server {serverUrl} -key {apiKey.substring(0, 16)}...
                  </code>
                </div>

                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>3. {t('agentDashboard.createConfig')} <span className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>(place config.json next to the agent binary)</span>:</p>
                <pre className={`rounded p-2 text-green-600 text-xs overflow-x-auto ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-300'}`}>
{configJson}
                </pre>
                <button
                  onClick={() => {
                    const blob = new Blob([configJson], { type: 'application/json' });
                    const dlUrl = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = dlUrl;
                    a.download = 'config.json';
                    document.body.appendChild(a);
                    a.click();
                    URL.revokeObjectURL(dlUrl);
                    document.body.removeChild(a);
                  }}
                  className="mt-3 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {t('agentDashboard.downloadConfig')}
                </button>
              </div>
            </div>

            <div className={`rounded p-3 mb-4 ${darkMode ? 'bg-yellow-900/50 border border-yellow-500' : 'bg-yellow-50 border border-yellow-300'}`}>
              <p className={`text-sm ${darkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
                <strong>{t('common.warning')}:</strong> {t('agentDashboard.approvalNote')}
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowDeployModal(false)}
                className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Queue & Command History Modal */}
      {showQueueModal && selectedAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <div>
                <h2 className="text-xl font-semibold">Agent Queue & Command History</h2>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {selectedAgent.name} - {selectedAgent.systemInfo?.hostname}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowQueueModal(false);
                  setSelectedAgent(null);
                  setAgentTasks([]);
                }}
                className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loadingTasks ? (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-blue-500 mb-4" />
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Loading tasks...</p>
                </div>
              ) : agentTasks.length === 0 ? (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faList} className={`text-4xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>No tasks found for this agent</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {agentTasks.map((task) => (
                    <div
                      key={task._id}
                      className={`rounded-lg border p-4 ${
                        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {/* Task Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {/* Status Icon */}
                          <div>
                            {task.status === 'completed' && (
                              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-xl" />
                            )}
                            {task.status === 'failed' && (
                              <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-xl" />
                            )}
                            {(task.status === 'pending' || task.status === 'sent') && (
                              <FontAwesomeIcon icon={faClock} className="text-yellow-500 text-xl" />
                            )}
                            {task.status === 'running' && (
                              <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-xl" />
                            )}
                          </div>

                          {/* Task Info */}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {task.type || 'Command'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                task.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                                task.status === 'failed' ? 'bg-red-500/20 text-red-500' :
                                task.status === 'running' ? 'bg-blue-500/20 text-blue-500' :
                                'bg-yellow-500/20 text-yellow-500'
                              }`}>
                                {task.status}
                              </span>
                            </div>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {new Date(task.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Command */}
                      {task.command && (
                        <div className="mb-3">
                          <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <FontAwesomeIcon icon={faTerminal} className="mr-1" />
                            Command
                          </div>
                          <pre className={`text-sm p-3 rounded overflow-x-auto ${
                            darkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                          }`}>{task.command}</pre>
                        </div>
                      )}

                      {/* Result/Output */}
                      {task.result && (
                        <div>
                          <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {task.status === 'failed' ? 'Error' : 'Output'}
                          </div>
                          <pre className={`text-sm p-3 rounded overflow-x-auto max-h-48 ${
                            task.status === 'failed'
                              ? darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'
                              : darkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
                          }`}>{task.result}</pre>
                        </div>
                      )}

                      {/* Execution Time */}
                      {task.completedAt && task.startedAt && (
                        <div className={`mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Execution time: {Math.round((new Date(task.completedAt) - new Date(task.startedAt)) / 1000)}s
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Total: {agentTasks.length} tasks
                {agentTasks.length > 0 && (
                  <span className="ml-3">
                    <span className="text-green-500">{agentTasks.filter(t => t.status === 'completed').length} completed</span>
                    <span className="mx-2">•</span>
                    <span className="text-yellow-500">{agentTasks.filter(t => ['pending', 'sent', 'running'].includes(t.status)).length} in queue</span>
                    <span className="mx-2">•</span>
                    <span className="text-red-500">{agentTasks.filter(t => t.status === 'failed').length} failed</span>
                  </span>
                )}
              </p>
              <button
                onClick={() => {
                  setShowQueueModal(false);
                  setSelectedAgent(null);
                  setAgentTasks([]);
                }}
                className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentDashboard;
